import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./Navbar.css";
import { HashLink } from 'react-router-hash-link';
import logo from "../assets/logo.png";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ===== Garments (level 1) =====
  const [garmentsOpen, setGarmentsOpen] = useState(false);

  // Garments → nested (level 2)
  const [menOpen, setMenOpen] = useState(false);
  const [womenOpen, setWomenOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [kidsOpen, setKidsOpen] = useState(false);

  // Auto-flip for Garments nested
  const [menSubmenuLeft, setMenSubmenuLeft] = useState(false);
  const [womenSubmenuLeft, setWomenSubmenuLeft] = useState(false);
  const [childrenSubmenuLeft, setChildrenSubmenuLeft] = useState(false);
  const [kidsSubmenuLeft, setKidsSubmenuLeft] = useState(false);

  // ===== Footwear (level 1) =====
  const [footwearOpen, setFootwearOpen] = useState(false);

  // Footwear → nested (level 2)
  const [menFWOpen, setMenFWOpen] = useState(false);
  const [womenFWOpen, setWomenFWOpen] = useState(false);
  const [childrenFWOpen, setChildrenFWOpen] = useState(false);
  const [kidsFWOpen, setKidsFWOpen] = useState(false);

  // Auto-flip for Footwear nested
  const [menFWLeft, setMenFWLeft] = useState(false);
  const [womenFWLeft, setWomenFWLeft] = useState(false);
  const [childrenFWLeft, setChildrenFWLeft] = useState(false);
  const [kidsFWLeft, setKidsFWLeft] = useState(false);

  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Framer motion variants
  const productVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
  };

  // Helper: flip nested submenu left if near viewport edge (desktop only)
  const flipIfOverflow = (e, setLeft) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setLeft(window.innerWidth - rect.right < 220);
  };

  // Helper: on mobile/tablet, toggle an accordion; on desktop do nothing
  const toggleMobileSubmenu = (state, setter) => {
    if (window.innerWidth <= 1024) setter(!state);
  };

  // Close mobile drawer after clicking any link
  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="nav-container">
        {/* Logo */}
        <Link to="/" className="logo" onClick={closeMobile}>
          <img src={logo} alt="Maya Exports Ltd" className="logo-img" />
        </Link>

        {/* Menu */}
        <ul className={`nav-links ${mobileOpen ? "open" : ""}`}>
          <li><Link to="/" onClick={closeMobile}>Home</Link></li>
          <li><Link to="/about" onClick={closeMobile}>About Us</Link></li>

          {/* Products Dropdown */}
          <li
            className="dropdown"
            onMouseEnter={() => window.innerWidth > 1024 && setDropdownOpen(true)}
            onMouseLeave={() => window.innerWidth > 1024 && setDropdownOpen(false)}
            onClick={() => window.innerWidth <= 1024 && setDropdownOpen(!dropdownOpen)}
          >
            <HashLink smooth to="/#mens-showcase" span className="drop-title">Products ▾</HashLink>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.ul
                  className="dropdown-menu"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={productVariants}
                >
                  {/* ============ GARMENTS ============ */}
                  <li
                    className="submenu-parent"
                    onMouseEnter={() => window.innerWidth > 1024 && setGarmentsOpen(true)}
                    onMouseLeave={() => window.innerWidth > 1024 && setGarmentsOpen(false)}
                  >
                    <div className="submenu-header">
                      <Link to="/products/garments" onClick={closeMobile} className="submenu-link">
                        Garments
                      </Link>

                      <span
                        className={`submenu-arrow ${garmentsOpen ? "open" : ""}`}
                        onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(garmentsOpen, setGarmentsOpen); }}
                        aria-label="Toggle garments submenu"
                      >▸</span>
                    </div>

                    <AnimatePresence>
                      {garmentsOpen && (
                        <motion.ul
                          className="submenu"
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={productVariants}
                        >
                          {/* Men */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) {
                                setMenOpen(true);
                                flipIfOverflow(e, setMenSubmenuLeft);
                              }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setMenOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/garments/men" onClick={closeMobile} className="submenu-link">
                                Men
                              </Link>
                              <span
                                className={`submenu-arrow ${menOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(menOpen, setMenOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {menOpen && (
                                <motion.ul
                                  className={`submenu nested ${menSubmenuLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/garments/men/jackets" onClick={closeMobile}>Men's Jackets</Link></li>
                                  <li><Link to="/products/garments/men/hoodies" onClick={closeMobile}>Men's Sweat Top & Hoodies</Link></li>
                                  <li><Link to="/products/garments/men/shirts" onClick={closeMobile}>Men's Shirts</Link></li>
                                  <li><Link to="/products/garments/men/tshirts" onClick={closeMobile}>Men's T-shirts</Link></li>
                                  <li><Link to="/products/garments/men/shorts" onClick={closeMobile}>Men's Shorts & Lowers</Link></li>
                                  <li><Link to="/products/garments/men/jeans" onClick={closeMobile}>Men's Jeans & Pants</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Women */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setWomenOpen(true); flipIfOverflow(e, setWomenSubmenuLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setWomenOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/garments/women" onClick={closeMobile} className="submenu-link">Women</Link>
                              <span
                                className={`submenu-arrow ${womenOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(womenOpen, setWomenOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {womenOpen && (
                                <motion.ul
                                  className={`submenu nested ${womenSubmenuLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/garments/women/jackets" onClick={closeMobile}>Women's Jackets</Link></li>
                                  <li><Link to="/products/garments/women/hoodies" onClick={closeMobile}>Women's Sweat Top & Hoodies</Link></li>
                                  <li><Link to="/products/garments/women/shorts" onClick={closeMobile}>Women's Short & Lower</Link></li>
                                  <li><Link to="/products/garments/women/dresses" onClick={closeMobile}>Women's Shirt</Link></li>
                                  <li><Link to="/products/garments/women/tshirt" onClick={closeMobile}>Women's T-Shirt</Link></li>
                                  <li><Link to="/products/garments/women/jeans" onClick={closeMobile}>Women's Jeans & Pants</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Children */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setChildrenOpen(true); flipIfOverflow(e, setChildrenSubmenuLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setChildrenOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/garments/children" onClick={closeMobile} className="submenu-link">Children</Link>
                              <span
                                className={`submenu-arrow ${childrenOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(childrenOpen, setChildrenOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {childrenOpen && (
                                <motion.ul
                                  className={`submenu nested ${childrenSubmenuLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/garments/children/jacket" onClick={closeMobile}>Child's Jacket</Link></li>
                                  <li><Link to="/products/garments/children/hoodies" onClick={closeMobile}>Child's Hoodies</Link></li>
                                  <li><Link to="/products/garments/children/shirts" onClick={closeMobile}>Child's Shirts</Link></li>
                                  <li><Link to="/products/garments/children/tshirts" onClick={closeMobile}>Child's T-shirts</Link></li>
                                  <li><Link to="/products/garments/children/shorts" onClick={closeMobile}>Child's Shorts & Lower</Link></li>
                                  <li><Link to="/products/garments/children/jeans" onClick={closeMobile}>Child's Jeans & Pant</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Kids */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setKidsOpen(true); flipIfOverflow(e, setKidsSubmenuLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setKidsOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/garments/kids" onClick={closeMobile} className="submenu-link">Kids</Link>
                              <span
                                className={`submenu-arrow ${kidsOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(kidsOpen, setKidsOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {kidsOpen && (
                                <motion.ul
                                  className={`submenu nested ${kidsSubmenuLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/garments/kids/sets" onClick={closeMobile}>Kid's Jacket</Link></li>
                                  <li><Link to="/products/garments/kids/sweattops&hoodie" onClick={closeMobile}>Kid's Sweat Tops & Hoodie</Link></li>
                                  <li><Link to="/products/garments/kids/short&lower" onClick={closeMobile}>Kid's Shorts & Lower</Link></li>
                                  <li><Link to="/products/garments/kids/shirts" onClick={closeMobile}>Kid's Shirt</Link></li>
                                  <li><Link to="/products/garments/kids/tshirts" onClick={closeMobile}>Kid's T-Shirt</Link></li>
                                  <li><Link to="/products/garments/kids/jeans" onClick={closeMobile}>Kid's Jeans & Pants</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>

                  {/* ============ FOOTWEAR ============ */}
                  <li
                    className="submenu-parent"
                    onMouseEnter={() => window.innerWidth > 1024 && setFootwearOpen(true)}
                    onMouseLeave={() => window.innerWidth > 1024 && setFootwearOpen(false)}
                  >
                    <div className="submenu-header">
                      <Link to="/products/footwear" onClick={closeMobile} className="submenu-link">Footwear</Link>
                      <span
                        className={`submenu-arrow ${footwearOpen ? "open" : ""}`}
                        onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(footwearOpen, setFootwearOpen); }}
                      >▸</span>
                    </div>

                    <AnimatePresence>
                      {footwearOpen && (
                        <motion.ul className="submenu" initial="hidden" animate="visible" exit="exit" variants={productVariants}>
                          {/* Men Footwear */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setMenFWOpen(true); flipIfOverflow(e, setMenFWLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setMenFWOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/footwear/men-footwear" onClick={closeMobile} className="submenu-link">Men</Link>
                              <span
                                className={`submenu-arrow ${menFWOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(menFWOpen, setMenFWOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {menFWOpen && (
                                <motion.ul
                                  className={`submenu nested ${menFWLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/footwear/men/sports-casual" onClick={closeMobile}>Sports & Casual</Link></li>
                                  <li><Link to="/products/footwear/men/slippers-sandals" onClick={closeMobile}>Slippers & Sandals</Link></li>
                                  <li><Link to="/products/footwear/men/office-shoes" onClick={closeMobile}>Office Shoes</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Women Footwear */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setWomenFWOpen(true); flipIfOverflow(e, setWomenFWLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setWomenFWOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/footwear/women-footwear" onClick={closeMobile} className="submenu-link">Women</Link>
                              <span
                                className={`submenu-arrow ${womenFWOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(womenFWOpen, setWomenFWOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {womenFWOpen && (
                                <motion.ul
                                  className={`submenu nested ${womenFWLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/footwear/women/sports-casual" onClick={closeMobile}>Sports & Casual</Link></li>
                                  <li><Link to="/products/footwear/women/slippers-sandals" onClick={closeMobile}>Slippers & Sandals</Link></li>
                                  <li><Link to="/products/footwear/women/office-shoes" onClick={closeMobile}>Office Shoes</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Children Footwear */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setChildrenFWOpen(true); flipIfOverflow(e, setChildrenFWLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setChildrenFWOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/footwear/children" onClick={closeMobile} className="submenu-link">Children</Link>
                              <span
                                className={`submenu-arrow ${childrenFWOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(childrenFWOpen, setChildrenFWOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {childrenFWOpen && (
                                <motion.ul
                                  className={`submenu nested ${childrenFWLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/footwear/children/sports-casual" onClick={closeMobile}>Sports & Casual</Link></li>
                                  <li><Link to="/products/footwear/children/slippers-sandals" onClick={closeMobile}>Slippers & Sandals</Link></li>
                                  <li><Link to="/products/footwear/children/office-shoes" onClick={closeMobile}>Office Shoes</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>

                          {/* Kids Footwear */}
                          <li
                            className="submenu-parent"
                            onMouseEnter={(e) => {
                              if (window.innerWidth > 1024) { setKidsFWOpen(true); flipIfOverflow(e, setKidsFWLeft); }
                            }}
                            onMouseLeave={() => window.innerWidth > 1024 && setKidsFWOpen(false)}
                          >
                            <div className="submenu-header">
                              <Link to="/products/footwear/kids" onClick={closeMobile} className="submenu-link">Kids</Link>
                              <span
                                className={`submenu-arrow ${kidsFWOpen ? "open" : ""}`}
                                onClick={(e) => { e.preventDefault(); toggleMobileSubmenu(kidsFWOpen, setKidsFWOpen); }}
                              >▸</span>
                            </div>

                            <AnimatePresence>
                              {kidsFWOpen && (
                                <motion.ul
                                  className={`submenu nested ${kidsFWLeft ? "open-left" : ""}`}
                                  initial="hidden" animate="visible" exit="exit" variants={productVariants}
                                >
                                  <li><Link to="/products/footwear/kids/sports-casual" onClick={closeMobile}>Sports & Casual</Link></li>
                                  <li><Link to="/products/footwear/kids/slippers-sandals" onClick={closeMobile}>Slippers & Sandals</Link></li>
                                  <li><Link to="/products/footwear/kids/office-shoes" onClick={closeMobile}>Office Shoes</Link></li>
                                </motion.ul>
                              )}
                            </AnimatePresence>
                          </li>
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </li>

                  {/* Other Product Categories */}
                  <li><Link to="/products/hometextiles" onClick={closeMobile}>Home Textiles</Link></li>
                  <li><Link to="/products/fabrics" onClick={closeMobile}>Fabrics</Link></li>
                  <li><Link to="/products/electronics" onClick={closeMobile}>Electronics & Appliances</Link></li>
                </motion.ul>
              )}
            </AnimatePresence>
          </li>

          <li><Link to="/eventspage" onClick={closeMobile}>Event</Link></li>
          <li><Link to="/career" onClick={closeMobile}>Career</Link></li>
          <li><Link to="/contact" onClick={closeMobile}>Contact Us</Link></li>
        </ul>

        {/* Mobile Menu Button */}
        <button
          className="hamburger"
          aria-label="Toggle menu"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? "✖" : "☰"}
        </button>
      </div>
    </nav>
  );
} 
























